import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Gift, ArrowRight, CheckCircle2, MessageCircle, Loader2, Sparkles, Users } from "lucide-react";
import { useGet } from "@/lib/hooks";
import { api } from "@/lib/api";
import { SectionHeading } from "@/components/wp/SectionHeading";
import { ProtectedImage } from "@/components/wp/ProtectedImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = { name: "", company: "", phone: "", email: "", occasion: "", quantity: "", budget: "", message: "" };

export default function CorporateGifting() {
  const { data: corp, loading } = useGet("/corporate");
  const [params] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { whatsapp_url }
  const formRef = useRef(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    const b = params.get("budget");
    if (b) set({ budget: b });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const pickCategory = (title) => { set({ occasion: title }); scrollToForm(); };
  const pickBudget = (b) => { set({ budget: b }); scrollToForm(); };

  const submit = async (e) => {
    e.preventDefault();
    for (const [k, label] of [["name", "your name"], ["company", "company name"], ["phone", "phone / WhatsApp"], ["occasion", "occasion / requirement"], ["quantity", "approximate quantity"], ["budget", "budget per gift"]]) {
      if (!form[k].trim()) { toast.error(`Please share ${label}.`); return; }
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/corporate/enquiry", form);
      setResult(data);
      toast.success("Thank you! We'll be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="wp-container wp-section">
        <Skeleton className="mx-auto h-10 w-2/3 rounded-lg" />
        <Skeleton className="mx-auto mt-4 h-5 w-1/2 rounded-lg" />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="aspect-[4/3] w-full rounded-wp-lg" />)}
        </div>
      </div>
    );
  }

  if (corp && corp.enabled === false) {
    return (
      <div className="wp-container wp-section text-center">
        <Gift className="mx-auto h-10 w-10 text-wp-rose-strong" />
        <h1 className="mt-4 font-display text-2xl text-wp-ink">Corporate Gifting</h1>
        <p className="mt-2 text-wp-ink-muted">This page is coming soon. Please check back shortly.</p>
      </div>
    );
  }

  const c = corp || {};
  const categories = c.categories || [];
  const budgets = c.budgets || [];

  return (
    <div data-testid="corporate-page">
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-wp-cream-100">
        <div className="wp-container py-12 md:py-16">
          <div className="mx-auto max-w-3xl text-center">
            <span className="wp-label inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1 text-xs text-wp-rose-strong shadow-wp-soft">
              <Sparkles className="h-3.5 w-3.5" /> Corporate Gifting
            </span>
            <h1 className="mt-5 font-display text-4xl leading-tight text-wp-rose-strong md:text-5xl" data-testid="corporate-headline">
              {c.headline_line1}
              <br />
              <span className="text-wp-ink">{c.headline_line2}</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base text-wp-ink-muted md:text-lg">{c.subheading}</p>
            <Button onClick={scrollToForm} className="mt-8 rounded-xl bg-wp-rose-strong px-8 py-6 text-base text-white hover:brightness-110" data-testid="corporate-hero-cta">
              {c.primary_cta || "Get a Custom Quote"} <ArrowRight className="ml-1.5 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* ================= CATEGORIES ================= */}
      <section className="wp-section">
        <div className="wp-container">
          <SectionHeading eyebrow="What we can curate" title="Gifting concepts for every team moment" align="center" />
          {c.sample_note && (
            <p className="mx-auto mt-3 max-w-2xl text-center text-sm italic text-wp-ink-muted" data-testid="corporate-sample-note">{c.sample_note}</p>
          )}
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((cat) => (
              <div key={cat.id} className="group flex flex-col overflow-hidden rounded-wp-lg border border-border bg-card shadow-wp-soft" data-testid={`corporate-category-${cat.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-wp-cream-100">
                  <ProtectedImage src={cat.image} alt={cat.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-display text-xl text-wp-ink">{cat.title}</h3>
                  <p className="mt-2 text-sm text-wp-ink-muted">{cat.copy}</p>
                  {(cat.products || []).length > 0 && (
                    <ul className="mt-4 flex flex-wrap gap-1.5">
                      {cat.products.map((p, i) => (
                        <li key={i} className="rounded-full bg-wp-cream-100 px-2.5 py-1 text-xs text-wp-ink">{p}</li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-5 flex-1" />
                  <Button variant="outline" onClick={() => pickCategory(cat.title)}
                    className="mt-2 w-full rounded-xl border-wp-rose-strong text-wp-rose-strong hover:bg-wp-cream-100" data-testid={`corporate-category-cta-${cat.id}`}>
                    {cat.cta || "Customize This Gift"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= BUDGET ================= */}
      {budgets.length > 0 && (
        <section className="wp-section bg-wp-cream-100">
          <div className="wp-container">
            <SectionHeading eyebrow="Flexible curation" title={c.budget_title || "Corporate Gifts for Every Budget"} align="center" />
            <div className="mx-auto mt-8 flex max-w-3xl flex-wrap justify-center gap-3">
              {budgets.map((b) => (
                <button key={b} onClick={() => pickBudget(b)}
                  className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${form.budget === b ? "border-wp-rose-strong bg-wp-rose-strong text-white" : "border-border bg-white text-wp-ink hover:border-wp-rose-strong hover:text-wp-rose-strong"}`}
                  data-testid={`corporate-budget-${budgets.indexOf(b)}`}>
                  {b}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================= BULK ================= */}
      <section className="wp-section">
        <div className="wp-container">
          <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 rounded-wp-lg border border-border bg-card p-8 text-center shadow-wp-soft md:flex-row md:text-left">
            <div className="grid h-14 w-14 flex-shrink-0 place-items-center rounded-full bg-wp-cream-100 text-wp-rose-strong">
              <Users className="h-7 w-7" />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-xl text-wp-ink">{c.bulk_title}</h3>
              <p className="mt-1 text-sm text-wp-ink-muted">{c.bulk_copy}</p>
            </div>
            <Button onClick={scrollToForm} className="rounded-xl bg-wp-rose-strong px-6 py-5 text-white hover:brightness-110" data-testid="corporate-bulk-cta">
              {c.primary_cta || "Get a Custom Quote"}
            </Button>
          </div>
        </div>
      </section>

      {/* ================= ENQUIRY FORM ================= */}
      <section ref={formRef} className="wp-section bg-wp-cream-100" id="corporate-enquiry">
        <div className="wp-container">
          <div className="mx-auto max-w-2xl">
            <SectionHeading eyebrow="Tell us your requirement" title="Get a Custom Quote" align="center" />
            {result ? (
              <div className="mt-8 rounded-wp-lg border border-border bg-card p-8 text-center shadow-wp-soft" data-testid="corporate-success">
                <CheckCircle2 className="mx-auto h-12 w-12 text-wp-success" />
                <h3 className="mt-4 font-display text-2xl text-wp-ink">Enquiry received!</h3>
                <p className="mt-2 text-wp-ink-muted">Thank you. Our team will reach out to help curate suitable options for your requirement.</p>
                <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <a href={result.whatsapp_url} target="_blank" rel="noreferrer" data-testid="corporate-whatsapp-button">
                    <Button className="rounded-xl bg-wp-rose-strong px-6 py-5 text-white hover:brightness-110">
                      <MessageCircle className="mr-2 h-5 w-5" /> Continue on WhatsApp
                    </Button>
                  </a>
                  <Button variant="ghost" onClick={() => { setResult(null); setForm(EMPTY); }} className="rounded-xl" data-testid="corporate-new-enquiry">
                    Submit another enquiry
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 grid gap-4 rounded-wp-lg border border-border bg-card p-6 shadow-wp-soft sm:grid-cols-2 md:p-8" data-testid="corporate-form">
                <div>
                  <Label className="mb-1.5 block text-sm">Name <span className="text-wp-rose-strong">*</span></Label>
                  <Input value={form.name} onChange={(e) => set({ name: e.target.value })} className="rounded-wp-md" data-testid="corporate-input-name" />
                </div>
                <div>
                  <Label className="mb-1.5 block text-sm">Company Name <span className="text-wp-rose-strong">*</span></Label>
                  <Input value={form.company} onChange={(e) => set({ company: e.target.value })} className="rounded-wp-md" data-testid="corporate-input-company" />
                </div>
                <div>
                  <Label className="mb-1.5 block text-sm">Phone / WhatsApp <span className="text-wp-rose-strong">*</span></Label>
                  <Input value={form.phone} onChange={(e) => set({ phone: e.target.value })} className="rounded-wp-md" data-testid="corporate-input-phone" />
                </div>
                <div>
                  <Label className="mb-1.5 block text-sm">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className="rounded-wp-md" data-testid="corporate-input-email" />
                </div>
                <div>
                  <Label className="mb-1.5 block text-sm">Occasion / Requirement <span className="text-wp-rose-strong">*</span></Label>
                  <Input value={form.occasion} onChange={(e) => set({ occasion: e.target.value })} placeholder="e.g. Diwali, onboarding, appreciation" className="rounded-wp-md" data-testid="corporate-input-occasion" />
                </div>
                <div>
                  <Label className="mb-1.5 block text-sm">Approximate Quantity <span className="text-wp-rose-strong">*</span></Label>
                  <Input value={form.quantity} onChange={(e) => set({ quantity: e.target.value })} placeholder="e.g. 50" className="rounded-wp-md" data-testid="corporate-input-quantity" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-1.5 block text-sm">Budget Per Gift <span className="text-wp-rose-strong">*</span></Label>
                  <Select value={form.budget} onValueChange={(v) => set({ budget: v })}>
                    <SelectTrigger className="rounded-wp-md" data-testid="corporate-input-budget"><SelectValue placeholder="Select a budget range" /></SelectTrigger>
                    <SelectContent>
                      {budgets.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-1.5 block text-sm">Message / Special Requirement</Label>
                  <Textarea rows={3} value={form.message} onChange={(e) => set({ message: e.target.value })} className="rounded-wp-md" data-testid="corporate-input-message" />
                </div>
                <div className="sm:col-span-2">
                  <Button type="submit" disabled={submitting} className="w-full rounded-xl bg-wp-rose-strong py-6 text-base text-white hover:brightness-110 disabled:opacity-60" data-testid="corporate-submit">
                    {submitting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending…</> : <>Get My Custom Quote <ArrowRight className="ml-1.5 h-5 w-5" /></>}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
